/**
 * storyArc.ts - Story Arc (Ki-Seung-Jeon-Gyeol) System
 *
 * Assigns narrative roles to each floor based on its stat profile.
 * Floors that match their assigned role grant a story bonus to SP earnings.
 *
 * Roles:
 *   intro      - Atmosphere build-up, low fear
 *   rising     - Fear and thrill ramp up
 *   climax     - Maximum fear and thrill
 *   resolution - Fun and relief
 */

import type { Floor, GameStateData } from './state.js';
import { calcRoomStats } from './roomStats.js';
import type { RoomStats } from './roomStats.js';

// ── Types ──────────────────────────────────────────────────

export type StoryRole = 'intro' | 'rising' | 'climax' | 'resolution' | 'none';

export type StoryGrade = 'S' | 'A' | 'B' | 'C' | 'D' | 'F';

export interface StoryArcResult {
  /** Bonus percentage (0-50) applied to SP earnings */
  bonus: number;
  /** Overall story arc grade */
  grade: StoryGrade;
  /** Role assigned to each built floor (same length as builtFloors) */
  roles: StoryRole[];
  /** Whether each floor matches its assigned role */
  roleMatches: boolean[];
  /** Number of matching floors */
  matchCount: number;
  /** Total built floors considered */
  totalRoles: number;
}

// ── Role Assignment ────────────────────────────────────────

/**
 * Assign story roles to built floors.
 * Floors are visited bottom-up (level 1 first), so:
 *   - First quarter = intro
 *   - Second quarter = rising
 *   - Third quarter = climax
 *   - Last quarter = resolution
 *
 * Returns roles array matching the order of building floors
 * (including nulls for empty floors as 'none').
 */
export function assignStoryRoles(building: Floor[]): StoryRole[] {
  const roles: StoryRole[] = [];
  const builtIndices: number[] = [];

  for (let i = 0; i < building.length; i++) {
    if (building[i].room !== null) {
      builtIndices.push(i);
    }
  }

  if (builtIndices.length < 4) {
    return building.map((f) => (f.room !== null ? 'none' : 'none'));
  }

  const count = builtIndices.length;
  const quarter = Math.floor(count / 4);
  const remainder = count % 4;

  // Distribute floors into 4 segments
  // If not evenly divisible, give extra floors to middle segments
  const segments = [quarter, quarter, quarter, quarter];
  if (remainder >= 1) segments[1]++;
  if (remainder >= 2) segments[2]++;
  if (remainder >= 3) segments[0]++;

  // Map each built floor to a role
  const roleMap = new Map<number, StoryRole>();
  let idx = 0;
  const roleNames: StoryRole[] = ['intro', 'rising', 'climax', 'resolution'];
  for (let seg = 0; seg < 4; seg++) {
    for (let j = 0; j < segments[seg]; j++) {
      roleMap.set(builtIndices[idx], roleNames[seg]);
      idx++;
    }
  }

  for (let i = 0; i < building.length; i++) {
    roles.push(roleMap.get(i) ?? 'none');
  }

  return roles;
}

// ── Role Matching ──────────────────────────────────────────

/**
 * Check if a room's stats match the expected profile for its story role.
 *
 * Thresholds (percentage of 100):
 *   intro:      atmosphere >= 40 AND fear <= 30
 *   rising:     fear >= 30 AND thrill >= 25
 *   climax:     fear >= 50 AND thrill >= 40
 *   resolution: fun >= 25
 */
function doesRoomMatchRole(stats: RoomStats, role: StoryRole): boolean {
  switch (role) {
    case 'intro':
      return stats.atmosphere >= 40 && stats.fear <= 30;
    case 'rising':
      return stats.fear >= 30 && stats.thrill >= 25;
    case 'climax':
      return stats.fear >= 50 && stats.thrill >= 40;
    case 'resolution':
      return stats.fun >= 25;
    case 'none':
      return false;
  }
}

// ── Bonus Calculation ──────────────────────────────────────

/**
 * Calculate the story arc bonus for the entire building.
 * Requires at least 4 built floors. Each matching floor counts toward the grade.
 *
 * Grading:
 *   S = 4/4 match = 50% bonus
 *   A = 3/4 match = 35% bonus
 *   B = 2/4 match = 25% bonus
 *   C = 1/4 match = 10% bonus
 *   D = 0/4 match = 0% bonus
 *   F = fewer than 4 built floors = 0% bonus
 */
export function calcStoryArc(building: Floor[], state: GameStateData): StoryArcResult {
  const roles = assignStoryRoles(building);

  const builtFloors = building.filter((f) => f.room !== null);
  if (builtFloors.length < 4) {
    return {
      bonus: 0,
      grade: 'F',
      roles,
      roleMatches: roles.map(() => false),
      matchCount: 0,
      totalRoles: 0,
    };
  }

  // Check each floor with a role
  const roleMatches: boolean[] = [];
  let matchCount = 0;

  // Count matches per unique role category (we only need one match per category)
  const categoryMatched: Record<string, boolean> = {
    intro: false,
    rising: false,
    climax: false,
    resolution: false,
  };

  for (let i = 0; i < building.length; i++) {
    const floor = building[i];
    const role = roles[i];

    if (role === 'none' || floor.room === null) {
      roleMatches.push(false);
      continue;
    }

    const stats = calcRoomStats(floor.room, state);
    const matches = doesRoomMatchRole(stats, role);
    roleMatches.push(matches);

    if (matches && !categoryMatched[role]) {
      categoryMatched[role] = true;
      matchCount++;
    }
  }

  // Grade based on how many of the 4 story categories are matched
  let grade: StoryGrade;
  let bonus: number;

  if (matchCount >= 4) {
    grade = 'S';
    bonus = 50;
  } else if (matchCount >= 3) {
    grade = 'A';
    bonus = 35;
  } else if (matchCount >= 2) {
    grade = 'B';
    bonus = 25;
  } else if (matchCount >= 1) {
    grade = 'C';
    bonus = 10;
  } else {
    grade = 'D';
    bonus = 0;
  }

  return {
    bonus,
    grade,
    roles,
    roleMatches,
    matchCount,
    totalRoles: 4,
  };
}
